function asBoolean(value, fallback = false) {
	if (value === undefined || value === null || value === '') {
		return fallback;
	}

	return value === 'true';
}

function asNumber(value, fallback) {
	const parsed = Number(value);

	return Number.isFinite(parsed) ? parsed : fallback;
}

function parseTelemetries(value) {
	if (!value) {
		return ['errors', 'performance', 'http'];
	}

	try {
		const parsed = JSON.parse(value);

		if (Array.isArray(parsed)) {
			return parsed;
		}
	} catch {
		// Fall back to comma-separated parsing.
	}

	return value
		.split(',')
		.map((telemetry) => telemetry.trim())
		.filter(Boolean);
}

function getNamespace() {
	return process.env.REACT_APP_CLOUDWATCH_RUM_NAMESPACE || 'cwr';
}

function getRegion() {
	return process.env.REACT_APP_CLOUDWATCH_RUM_APP_MONITOR_REGION;
}

function canUseBrowserRuntime() {
	return typeof window !== 'undefined' && typeof document !== 'undefined';
}

function hasRequiredConfig() {
	return Boolean(
		process.env.REACT_APP_CLOUDWATCH_RUM_APP_MONITOR_ID && getRegion()
	);
}

function canAuthenticate() {
	if (process.env.REACT_APP_CLOUDWATCH_RUM_IDENTITY_POOL_ID) {
		return true;
	}

	return (
		asBoolean(process.env.REACT_APP_CLOUDWATCH_RUM_SIGNING, true) === false
	);
}

function canUse() {
	if (!canUseBrowserRuntime()) {
		return false;
	}

	if ((process.env.REACT_APP_ENV || 'local') === 'local') {
		return false;
	}

	if (!hasRequiredConfig()) {
		return false;
	}

	return canAuthenticate();
}

function getClientUrl() {
	if (process.env.REACT_APP_CLOUDWATCH_RUM_CLIENT_URL) {
		return process.env.REACT_APP_CLOUDWATCH_RUM_CLIENT_URL;
	}

	return `https://client.rum.${getRegion()}.amazonaws.com/1.0.2/cwr.js`;
}

function getRumConfig() {
	return {
		allowCookies: asBoolean(
			process.env.REACT_APP_CLOUDWATCH_RUM_ALLOW_COOKIES,
			true
		),
		enableXRay: asBoolean(
			process.env.REACT_APP_CLOUDWATCH_RUM_ENABLE_XRAY,
			false
		),
		endpoint: process.env.REACT_APP_CLOUDWATCH_RUM_ENDPOINT || undefined,
		guestRoleArn:
			process.env.REACT_APP_CLOUDWATCH_RUM_GUEST_ROLE_ARN || undefined,
		identityPoolId:
			process.env.REACT_APP_CLOUDWATCH_RUM_IDENTITY_POOL_ID || undefined,
		sessionSampleRate: asNumber(
			process.env.REACT_APP_CLOUDWATCH_RUM_SESSION_SAMPLE_RATE,
			1
		),
		signing: asBoolean(process.env.REACT_APP_CLOUDWATCH_RUM_SIGNING, true),
		telemetries: parseTelemetries(
			process.env.REACT_APP_CLOUDWATCH_RUM_TELEMETRIES
		),
	};
}

function bootstrap(settings) {
	const client = window.AwsRumClient || {
		c: settings.config,
		['i']: settings.applicationId,
		n: settings.namespace,
		q: [],
		r: settings.region,
		v: settings.version,
	};

	window.AwsRumClient = client;

	if (typeof window[settings.namespace] !== 'function') {
		window[settings.namespace] = function (command, payload) {
			client.q.push({
				c: command,
				p: payload,
			});
		};
	}

	const existingScript = document.querySelector(
		`script[data-rum-namespace="${settings.namespace}"]`
	);

	if (existingScript) {
		return;
	}

	const script = document.createElement('script');
	script.async = true;
	script.dataset.rumNamespace = settings.namespace;
	script.src = settings.source;
	document.head.insertBefore(script, document.head.firstChild);
}

let initialized = false;

const CloudWatchMonitor = {
	captureException(error) {
		CloudWatchMonitor.initialize();
		window[getNamespace()]?.('recordError', error);
	},

	initialize() {
		if (initialized || !canUse()) {
			return;
		}

		try {
			bootstrap({
				applicationId:
					process.env.REACT_APP_CLOUDWATCH_RUM_APP_MONITOR_ID,
				config: getRumConfig(),
				namespace: getNamespace(),
				region: getRegion(),
				source: getClientUrl(),
				version: process.env.REACT_APP_VERSION || '0.1.0',
			});
			initialized = true;
		} catch (error) {
			/* eslint-disable-next-line no-console -- Monitoring bootstrap failures should not crash the app */
			console.error('Failed to initialize CloudWatch RUM.', error);
		}
	},
};

export default CloudWatchMonitor;
